import { isNil, pluck } from 'ramda';
import { __, _n, sprintf } from '@wordpress/i18n';
import { queryClient, queryKeys } from '../../lib/query-client';
import ProjectService from '../../service/project-service';
import PermissionsService from '../../service/permissions-service';
import displayNetworkError from '../../helpers/displayNetworkError';
import handleSuccessAndDisplay from '../../helpers/handleSuccessAndDisplay';
import handleErrorAndDisplay from '../../helpers/handleErrorAndDisplay';
import { storeSet } from '../index';

export const makeProjectActions = (set, get) => {
    const fetchProjectsImpl = async () => {
        set('projectsLoading', true);
        set('projectsError', null);

        try {
            const data = await queryClient.fetchQuery({
                queryKey: queryKeys.projects,
                queryFn: ProjectService.getProjectsAsync,
            });

            set('projects', data);
            set('projectsLoading', false);
        } catch (error) {
            set('projectsError', error);
            set('projectsLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const refetchProjectsImpl = async () => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
        await fetchProjectsImpl();
    };

    const fetchProjectImpl = async (id) => {
        set('currentProjectLoading', true);
        set('currentProjectError', null);

        try {
            const data = await queryClient.fetchQuery({
                queryKey: queryKeys.project(id),
                queryFn: () => ProjectService.getProjectAsync(id),
            });

            set('currentProject', data);
            set('currentProjectLoading', false);
        } catch (error) {
            set('currentProjectError', error);
            set('currentProjectLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const fetchProjectTeamImpl = async (id) => {
        set('teamLoading', true);
        set('teamError', null);

        try {
            const data = await queryClient.fetchQuery({
                queryKey: queryKeys.projectTeam(id),
                queryFn: () => ProjectService.getProjectTeamAsync(id),
            });

            set('team', data);
            set('teamLoading', false);
        } catch (error) {
            set('teamError', error);
            set('teamLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const refetchProjectTeamImpl = async (id) => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(id) });
        await fetchProjectTeamImpl(id);
    };

    return {
        fetchProjectsImpl,
        refetchProjectsImpl,
        fetchProjectImpl,
        fetchProjectTeamImpl,
        refetchProjectTeamImpl,

        fetchProjects: fetchProjectsImpl,
        refetchProjects: refetchProjectsImpl,
        fetchProject: fetchProjectImpl,
        fetchProjectTeam: fetchProjectTeamImpl,
        refetchProjectTeam: refetchProjectTeamImpl,

        createUpdateProject: async (formData) => {
            try {
                await ProjectService.createUpdateAsync(formData);

                await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
                await refetchProjectsImpl();
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        patchProject: async (projectId, body) => {
            try {
                await ProjectService.patchProjectAsync(projectId, body);

                await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
                await queryClient.invalidateQueries({ queryKey: queryKeys.project(projectId) });
                await refetchProjectsImpl();
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        archiveProject: async (projectId) => {
            try {
                await ProjectService.patchProjectAsync(projectId, { archived: true });

                handleSuccessAndDisplay(__('Progetto archiviato', 'soundbetween'));

                await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
                await refetchProjectsImpl();
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        markProjectSeen: async (projectId) => {
            // Nothing to clear — skip the round trip so opening a project with
            // no unread messages stays a single request.
            const project = get('projects').find(o => o.id === projectId);

            if (!project || !project.unreadMessagesCount) {
                return;
            }

            try {
                await ProjectService.markProjectSeenAsync(projectId);

                await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
                await refetchProjectsImpl();
            } catch (error) {
                // A failed "seen" call only means a stale badge — never block the
                // project the user just opened over it.
                displayNetworkError(error, { silent: true });
            }
        },

        createInvitationsBulkForProject: async (projectId, formData) => {
            try {
                const results = await ProjectService.createProjectTeamInvitationsBulkAsync(projectId, formData);
                const errors = pluck('error', results).filter(s => !isNil(s));

                if (errors.length === 0) {
                    handleSuccessAndDisplay(__('Invitations sent!', 'soundbetween'));
                    storeSet('refetchProjectTeam', projectId);
                } else {
                    errors.map(s => handleErrorAndDisplay(s));
                }

                return results;
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        /**
         * Assigns a cue-sheet item to one or more users, one call per member.
         *
         * The cue sheet itself no longer goes through here: a cue holds a list
         * of assignees and `setCueSheetAssignees` (cuesheet-actions.js) writes
         * the whole list in one replace-set call. This stays for the plain
         * `POST /permissions/assign` endpoint, which grants access to a single
         * item without touching the list.
         *
         * The requests go out in parallel, but the feedback is collapsed into a
         * single toast and a single team refresh: one of each per member buries
         * the screen when a whole group is assigned at once.
         *
         * @param {string} projectId
         * @param {Array|string} userIds - one user id or a list of them
         * @param {object} params - { cueSheetId, note }; an empty note is
         *   dropped from the query string by the network layer.
         */
        assignPermissions: async (projectId, userIds, { cueSheetId, note } = {}) => {
            const ids = (Array.isArray(userIds) ? userIds : [userIds]).filter(id => !isNil(id));

            if (ids.length === 0) {
                return { assigned: 0, failed: 0 };
            }

            const results = await Promise.allSettled(ids.map(userId => (
                PermissionsService.permissionsAssign([
                    ['userId', userId],
                    ['cueSheetId', cueSheetId],
                    ['note', note]
                ])
            )));

            const rejected = results.filter(r => r.status === 'rejected');
            const assigned = results.length - rejected.length;

            if (assigned > 0) {
                handleSuccessAndDisplay(sprintf(
                    _n('%d membro assegnato', '%d membri assegnati', assigned, 'soundbetween'),
                    assigned
                ));

                await queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(projectId) });
                await queryClient.invalidateQueries({ queryKey: queryKeys.userMe });

                await refetchProjectTeamImpl(projectId);
            }

            // Parallel calls that fail usually fail for the same reason, so only
            // the first one is shown rather than one toast per member.
            if (rejected.length > 0) {
                displayNetworkError(rejected[0].reason);
            }

            // Nothing got through: let the caller keep its form open. Flagged as
            // handled because the error above has already been shown.
            if (assigned === 0) {
                throw Object.assign(new Error('Assignment failed'), {
                    handled: true,
                    cause: rejected[0]?.reason
                });
            }

            return { assigned, failed: rejected.length };
        },

        unassignPermissions: async (projectId, queryParams) => {
            try {
                await PermissionsService.permissionsUnAssign(queryParams);

                handleSuccessAndDisplay(__('Assegnazione rimossa', 'soundbetween'));

                await queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(projectId) });
                await queryClient.invalidateQueries({ queryKey: queryKeys.userMe });

                await refetchProjectTeamImpl(projectId);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },
    };
};
